<script setup lang="ts">
const {
  permission,
  isSupported,
  canSendNotifications,
  isSubscribed,
  notifications,
  unreadCount,
  requestPermission,
  subscribe,
  unsubscribe,
  markAllAsRead,
  clearAllNotifications,
  checkExistingSubscription
} = useNotifications()

const requestingPermission = ref(false)
const subscribing = ref(false)
const unsubscribing = ref(false)

// Get VAPID public key from environment or config
const vapidPublicKey = ref(
  'BOysiemR2WI9Wmy4_F0SbKXxAorFC0Ga90I5jmiybhHmLdFztNKlYLsnD7gNGRpZIh0HAbPvxl3ezAE_0TW_jB0'
) // Replace with actual key

const subscriptionStatus = computed(() => {
  if (isSubscribed.value) {
    return 'subscribedToPushNotifications'
  }
  return 'subscribeToPushNotifications'
})

onMounted(async () => {
  await checkExistingSubscription()
})

const requestNotificationPermission = async () => {
  try {
    requestingPermission.value = true
    await requestPermission()
  } catch {
  } finally {
    requestingPermission.value = false
  }
}

const subscribeToNotifications = async () => {
  try {
    subscribing.value = true
    await subscribe(vapidPublicKey.value)
  } catch {
  } finally {
    subscribing.value = false
  }
}

const unsubscribeFromNotifications = async () => {
  try {
    unsubscribing.value = true
    await unsubscribe()
  } catch {
  } finally {
    unsubscribing.value = false
  }
}
</script>

<template>
  <div v-if="isSupported" class="notification-panel">
    <!-- Permission Request Button -->
    <div v-if="permission !== 'granted'" class="p-4 border-2 border-primary rounded-lg mb-4">
      <h3 class="text-lg font-medium text-blue-900 mb-2">
        {{ $t('components.notificationPanel.grantAuthorization') }}
      </h3>
      <p class="mb-3">{{ $t('components.notificationPanel.grantAuthorizationDetail') }}</p>
      <Button
        :loading="requestingPermission"
        :label="$t('components.notificationPanel.enableNotifications')"
        icon="pi pi-bell"
        icon-class="text-white"
        class="p-button-primary"
        @click="requestNotificationPermission"
      />
    </div>

    <!-- Subscription Management -->
    <div v-if="canSendNotifications" class="p-4 border-2 border-primary rounded-lg mb-4">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h3 class="text-lg font-medium">{{ $t('components.notificationPanel.subscription') }}</h3>
          <p class="text-green-700 mt-1">
            {{ $t('components.notificationPanel.' + subscriptionStatus) }}
          </p>
        </div>
        <Button
          v-if="!isSubscribed"
          :loading="subscribing"
          :label="$t('components.notificationPanel.subscribe')"
          severy="primary"
          class="min-w-fit"
          @click="subscribeToNotifications"
        />
        <Button
          v-else
          :loading="unsubscribing"
          :label="$t('components.notificationPanel.unsubscribe')"
          severity="danger"
          class="min-w-fit"
          @click="unsubscribeFromNotifications"
        />
      </div>
    </div>

    <!-- Notifications List -->
    <div v-if="notifications.length > 0" class="notification-list">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium">
          Notifications
          <Badge v-if="unreadCount > 0" :value="unreadCount" severity="danger" />
        </h3>
        <div class="flex gap-2">
          <Button
            v-if="unreadCount > 0"
            label="Mark all read"
            size="small"
            text
            @click="markAllAsRead"
          />
          <Button
            label="Clear all"
            size="small"
            text
            severity="danger"
            @click="clearAllNotifications"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notification-panel {
  max-width: 600px;
}

.notification-item:hover {
  @apply bg-gray-100 border-gray-300;
}

.notification-item.bg-blue-50:hover {
  @apply bg-blue-100 border-blue-300;
}
</style>
